import React, { useEffect, useState } from 'react';
import { useRazorpay } from 'react-razorpay';
import { RAZORPAY_CONFIG, formatAmount, getDefaultPrefill, getEnvironmentConfig } from '../config/razorpay';

const RazorpayPaymentButton = ({
  amount, // in paise
  orderId = null, // optional order ID from your backend
  description = 'Payment for services',
  userInfo = {},
  onSuccess = () => {},
  onFailure = () => {},
  disabled = false,
  buttonText = 'Pay Now',
  className = '',
  children = null, // Custom button content
}) => {
  const { error, isLoading, Razorpay } = useRazorpay();
  const { key_id } = getEnvironmentConfig();
  const [sdkReady, setSdkReady] = useState(false);

  // Check if Razorpay SDK is ready
  useEffect(() => {
    const checkSDK = () => {
      if (Razorpay && !isLoading && !error) {
        setSdkReady(true);
      } else if (window.Razorpay) {
        // Fallback: check for global Razorpay object
        setSdkReady(true);
      } else {
        setSdkReady(false);
      }
    };

    checkSDK();
    
    // Also check after a delay in case SDK loads asynchronously
    const timer = setTimeout(checkSDK, 1000);
    return () => clearTimeout(timer);
  }, [Razorpay, isLoading, error]);

  // Retry loading if there's an error or after initial load
  useEffect(() => {
    if (error || (!sdkReady && !isLoading)) {
      console.warn('Razorpay SDK issue, checking for global fallback');
      const timer = setTimeout(() => {
        if (window.Razorpay) {
          setSdkReady(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [error, sdkReady, isLoading]);

  const handlePayment = () => {
    // Check for Razorpay in multiple ways
    const razorpayInstance = Razorpay || window.Razorpay;
    
    if (!razorpayInstance) {
      console.error('Razorpay SDK not loaded');
      onFailure({ error: 'Payment system not available. Please refresh the page and try again.' });
      return;
    }

    const { isProduction } = getEnvironmentConfig();
    
    // In development, show a warning about live keys
    if (!isProduction) {
      console.warn('⚠️ Using live Razorpay keys in development. Some features may not work as expected.');
    }

    const options = {
      key: key_id,
      amount: amount,
      currency: RAZORPAY_CONFIG.currency,
      name: RAZORPAY_CONFIG.company_name,
      description: description,
      image: '', // Leave empty to avoid mixed content issues in development
      order_id: orderId, // Optional: Generate this from your backend
      handler: function (response) {
        console.log('Payment successful:', response);
        // Call success callback with payment details
        onSuccess({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
        });
      },
      prefill: getDefaultPrefill(userInfo),
      notes: {
        address: 'Corporate Office',
        purpose: description,
      },
      theme: {
        color: RAZORPAY_CONFIG.theme_color,
      },
      modal: {
        ondismiss: function() {
          console.log('Payment modal closed');
          onFailure({ error: 'Payment cancelled by user' });
        }
      }
    };

    try {
      const rzpInstance = new razorpayInstance(options);
      
      rzpInstance.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        onFailure({
          error: response.error.description,
          code: response.error.code,
          source: response.error.source,
          step: response.error.step,
          reason: response.error.reason,
        });
      });

      rzpInstance.open();
    } catch (err) {
      console.error('Error opening Razorpay checkout:', err);
      onFailure({ error: 'Failed to open payment gateway. Please try again.' });
    }
  };

  // Simple traditional button styling with deep black background
  const defaultButtonClass = `
    w-full bg-black hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-lg 
    transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
  `;

  return (
    <div className="w-full">
      {/* Error display - only show critical errors */}
      {!sdkReady && !isLoading && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 text-yellow-700 rounded-lg">
          <p className="text-sm mb-2">Payment system is loading...</p>
          <button 
            onClick={() => {
              setSdkReady(false);
              if (window.Razorpay) setSdkReady(true);
            }}
            className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state - only show when initially loading */}
      {isLoading && !sdkReady && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-300 text-blue-700 rounded-lg">
          <p className="text-sm">Loading payment system...</p>
        </div>
      )}

      {/* Simple Payment Button */}
      <button
        onClick={handlePayment}
        disabled={disabled || (!sdkReady && isLoading)}
        className={className || defaultButtonClass}
      >
        {children || (
          <span className="text-lg flex items-center justify-center gap-2">
            {/* Credit Card Icon */}
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 6h16v2H4V6zm0 4h16v8H4v-8zm2 2h4v2H6v-2z"/>
            </svg>
            {buttonText} {formatAmount(amount)}
          </span>
        )}
      </button>

      {/* Minimal security info */}
      <div className="text-center mt-2 text-xs text-gray-500">
        Secure payment powered by Razorpay
      </div>
    </div>
  );
};

export default RazorpayPaymentButton;
